const pad2 = n => String(n).padStart(2, '0')

// Parse a value that may be a date-only column as LOCAL midnight, not UTC
// midnight.  The pg driver hands us `time_entries.date` etc. as a Date at
// UTC midnight, which JSON.stringify serializes as `"2026-06-16T00:00:00.000Z"`.
// Plain `new Date(...)` then parses that as UTC midnight → in Pacific (UTC-7)
// renders as Jun 15 — the "off by one day" bug Steve hit on the matter
// billing tab.  Real timestamps (events.start_time, communications.comm_date)
// carry an actual time-of-day in the T-suffix and must pass through
// unchanged.  Detection: if the suffix is exactly `T00:00:00(.000)?Z` it's a
// date-only column; pull the YYYY-MM-DD prefix and anchor to local midnight.
export function parseDateOnly(input) {
  if (!input) return null
  if (input instanceof Date) return input
  if (typeof input === 'string') {
    // Bare YYYY-MM-DD (no time) — local midnight
    let m = input.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    // Or YYYY-MM-DD followed by an explicit UTC-midnight suffix (the pg
    // serialization of a DATE column).  Includes the millisecond-precision
    // form `T00:00:00.000Z` that node-pg actually emits.
    if (!m) m = input.match(/^(\d{4})-(\d{2})-(\d{2})T00:00:00(?:\.0+)?Z$/)
    if (m) {
      return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]))
    }
  }
  return new Date(input)
}

// When the start of an event changes, shift the end by the same delta so
// the duration is preserved.  Standard Google Calendar / Outlook behavior.
// Inputs are the `datetime-local` string values ("YYYY-MM-DDTHH:MM") that
// React's onChange hands us; output is the same shape.  Edge cases:
// - End empty → return empty (don't fabricate an end the user didn't set)
// - Old start missing or old delta non-finite → fall back to 1-hour duration
// - Old delta negative (the broken state Darian flagged in #68 — end<start)
//   → also fall back to 1-hour duration so the new value lands somewhere
//   sensible instead of preserving the invalid relationship.
// The arithmetic is on the WALL CLOCK the inputs show (the firm's), never the
// browser's zone: the strings were parsed as browser-local instants, so in a
// browser that observes daylight saving a 7-day span (00:00 → 00:00) moved
// across the November change came back 00:00 → 23:00 the day before.  For an
// Amicus deadline to-do that changed which day it is due (lib/eventDaySpan.js
// isDeadlineTodo needs 00:00 → 00:00; verifier 2026-10-21).
// format.adjustEnd.test.js.
const WALL_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/
function wallClockMs (value) {
  const m = typeof value === 'string' ? value.match(WALL_RE) : null
  if (!m) return NaN
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6] || 0))
}
export function adjustEndOnStartChange(oldStart, newStart, oldEnd) {
  if (!newStart || !oldEnd) return oldEnd
  const oldStartMs = oldStart ? wallClockMs(oldStart) : NaN
  const oldEndMs = wallClockMs(oldEnd)
  const newStartMs = wallClockMs(newStart)
  if (!Number.isFinite(newStartMs) || !Number.isFinite(oldEndMs)) return oldEnd
  let durationMs = Number.isFinite(oldStartMs) ? oldEndMs - oldStartMs : NaN
  if (!Number.isFinite(durationMs) || durationMs < 0) durationMs = 60 * 60 * 1000
  // UTC fields of an as-if-UTC value = the wall clock (no zone involved).
  return new Date(newStartMs + durationMs).toISOString().slice(0, 16)
}

// Round to a 0.1-hour billable increment per firm policy: 6-min units with a
// 0.1 minimum on any non-zero work.  Amicus-imported entries can carry
// exact-seconds hours like 0.04, 0.25, 1.21 — the firm bills in 6-min
// increments so we round on display rather than mutate the stored value.
//
// Uses Math.ceil (not Math.round) so a 0.04hr entry surfaces as 0.1 instead
// of vanishing to 0.0 — matches the existing roundToSixMin helper in
// TimeEntries.js and the standard billing convention (you don't give clients
// free 6-min increments by rounding down).
export function roundBillable(hours) {
  const n = parseFloat(hours)
  if (isNaN(n) || n <= 0) return 0
  return Math.max(0.1, Math.ceil(n * 10) / 10)
}

export function formatDate(input, format = 'MM/DD/YYYY') {
  if (!input) return ''
  const d = parseDateOnly(input)
  if (!d || isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = pad2(d.getMonth() + 1)
  const day = pad2(d.getDate())
  switch (format) {
    case 'DD/MM/YYYY': return `${day}/${m}/${y}`
    case 'YYYY-MM-DD': return `${y}-${m}-${day}`
    case 'MM/DD/YYYY':
    default: return `${m}/${day}/${y}`
  }
}

export function formatTime(input, format = '12h') {
  if (!input) return ''
  const d = input instanceof Date ? input : new Date(input)
  if (isNaN(d.getTime())) return ''
  const h24 = d.getHours()
  const m = pad2(d.getMinutes())
  if (format === '24h') return `${pad2(h24)}:${m}`
  const period = h24 >= 12 ? 'PM' : 'AM'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${m} ${period}`
}

export function formatDateTime(input, dateFormat = 'MM/DD/YYYY', timeFormat = '12h') {
  const d = formatDate(input, dateFormat)
  const t = formatTime(input, timeFormat)
  return d && t ? `${d} ${t}` : d || t
}


// How the "Method" cell of an invoice's Payments & credits table reads one
// allocation row.
//
// payment_method is what the money arrived as; payment_source is provenance
// ('stripe' = the webhook recorded it, 'manual' = a human keyed it).  Two
// separate honesty problems, both live:
//
//  1. 'unknown' is an Amicus import artifact meaning "no method was recorded",
//     not a method.  292 of this firm's 295 receipts carry it, and the cell has
//     `capitalize`, so the column read as a wall of the literal word "Unknown"
//     — a data absence presented as if it were a fact.  An em dash says the
//     true thing.
//  2. payment_method is nullable with no DB default, so a row can have no
//     method at all and render an unreadable blank cell.
//
// Deliberately NOT prefixing the channel onto the method.  The only writer of
// Stripe receipts (api/src/routes/stripeWebhook.js) hardcodes
// payment_method = 'Stripe' beside payment_source = 'stripe', so a "Stripe · "
// prefix would render "Stripe · Stripe".  The source is consulted only where
// the method has nothing to say — the one case it is not redundant.
export function allocationMethodLabel(a) {
  const raw = String((a && a.payment_method) || '').trim()
  if (raw && raw.toLowerCase() !== 'unknown') return raw.replace('_', ' ')
  return (a && a.payment_source) === 'stripe' ? 'Stripe' : '\u2014'
}


// The same honesty contract as `allocationMethodLabel`, but for a caller that
// is building a SENTENCE rather than filling a table cell: returns null when
// the stored method says nothing, so the segment can be dropped instead of
// printing a placeholder mid-sentence.
//
// Why this exists: `invoices.payment_method` carries the identical Amicus
// 'unknown' artifact the receipts do — measured on production, 222 of 225
// paid/partial invoices — so the green "Payment Received" banner on the invoice
// page read "Unknown — March 4, 2024", asserting a payment method that was
// never recorded.  That banner sits a few inches below the Payments & credits
// table, which already renders the honest em dash, so the page was telling a
// reader two different things about one fact.  Both now route through one
// contract and cannot drift apart.
export function paymentMethodOrNull(a) {
  const label = allocationMethodLabel(a)
  return label === '—' ? null : label
}


// ── Accounting period-lock sidebar badge (2026-09-16) ────────────────────────
//
// `firm_settings.accounting_lock_date` is the wall Ship 7d put in front of every
// dated financial mutation: a time entry, cost, invoice, trust deposit or
// disbursement on or before that date is refused.  The sidebar badge exists so a
// bookkeeper can see WHERE the wall is without opening the Accounting page.
//
// Two rules live here rather than at the call site, because both fail silently
// when inlined:
//
//  1. THE DATE HELPER.  accounting_lock_date is a DATE column, so node-pg
//     serializes it as UTC midnight ('2026-06-30T00:00:00.000Z').  `new Date()`
//     on that renders the PREVIOUS day anywhere west of UTC — this firm is
//     UTC-7/-8, so the badge would read "closed through Jun 29" while the books
//     are actually closed through Jun 30.  Wrong by a day, on a compliance
//     surface, in the direction that makes a locked period look open.
//     parseDateOnly() anchors to local midnight; same class as the four sites
//     fixed on 2026-09-02.
//
//  2. THE LABEL CARRIES THE DAY, not just the month.  A month-only pill
//     ("Jun '26") is a lie whenever the close lands mid-month, and mid-month is
//     legal: POST /api/accounting/period/close constrains only
//     `through_date < today` and `through_date > the previous close`, with no
//     month-end requirement.  A firm closed through Jul 1 would read as "July is
//     closed" when 30 days of July are still open.
//
// Month names are a literal table, not toLocaleDateString: the output must not
// depend on the ICU locale of whatever machine renders it, and the test must be
// able to assert an exact string.
const LOCK_MONTH_ABBREV = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const LOCK_MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December']

function lockDateParts(v) {
  const d = parseDateOnly(v)
  if (!d || Number.isNaN(d.getTime())) return null
  return { m: d.getMonth(), day: d.getDate(), y: d.getFullYear() }
}

// Short pill text, e.g. "Jun 30 '26".  null when no period has been closed —
// callers render nothing rather than an empty badge.
export function lockDateBadgeLabel(v) {
  const p = lockDateParts(v)
  if (!p) return null
  return `${LOCK_MONTH_ABBREV[p.m]} ${p.day} '${pad2(p.y % 100)}`
}

// The hover/screen-reader sentence.  Spelled out in full because the pill's
// two-digit year and abbreviated month are a glance, not a record.
export function lockDateBadgeTitle(v) {
  const p = lockDateParts(v)
  if (!p) return null
  return `Books closed through ${LOCK_MONTH_FULL[p.m]} ${p.day}, ${p.y}`
}
